import React, { FC, useState } from 'react';
import { VStack } from '@cardboard-ui/react';
import { PDFTronFileRenderer } from '../PDFTronFileRenderer';
import { FileRenderer_node$data } from '../__generated__/FileRenderer_node.graphql';
import { graphql, useFragment } from 'utils/graphClient';
import { ManagersSteps } from '../PDFTronFileRenderer/ManagersSteps';
import { SignersSteps } from '../PDFTronFileRenderer/SignersSteps';
import { SignatureStatusFileRenderer_node$key } from './__generated__/SignatureStatusFileRenderer_node.graphql';
import { useAuthenticatedSession } from 'utils/sessionProvider';

interface SignatureStatusFileRendererProps {
  node: FileRenderer_node$data;
}

const signatureStatusFragment = graphql`
  fragment SignatureStatusFileRenderer_node on Node {
    __typename
    id
    ... on SignableInterface {
      isSignable
      signatureEnvelope {
        id
        status
        signatures {
          nodes {
            id
            member {
              id
            }
            status
          }
        }
        currentFile {
          __typename
          id
          name
          fileName
          fileNameWithExtension: fileName(ensureExtension: true)
          mimeType
          url
          size
        }
        canSign {
          value
        }
        canDelete {
          value
        }
      }
    }
    ...ManagersSteps_node
  }
`;

export const SignatureStatusFileRenderer: FC<
  SignatureStatusFileRendererProps
> = ({ node }) => {
  const { id, signatureEnvelope, ...fragments } = useFragment(
    signatureStatusFragment,
    node as SignatureStatusFileRenderer_node$key,
  );
  const { isCurrentMember } = useAuthenticatedSession();

  const [isSigningStarted, setIsSigningStarted] = useState(false);

  const currentSignature = signatureEnvelope?.signatures.nodes.find((node) =>
    isCurrentMember(node.member),
  );

  const completedSignatureCount = signatureEnvelope?.signatures.nodes.filter(
    ({ status: signatureStatus }) => signatureStatus !== 'WAITING',
  );

  const canSignEnvelope = signatureEnvelope?.canSign.value;
  const canDeleteEnvelope = signatureEnvelope?.canDelete.value;

  return (
    <VStack w="100%">
      {signatureEnvelope && (
        <VStack
          w="100%"
          p={2}
          backgroundColor="gray.50"
          _dark={{
            backgroundColor: 'gray.700',
          }}
        >
          {canDeleteEnvelope && !isSigningStarted && (
            <ManagersSteps node={fragments} />
          )}
          {canSignEnvelope && (
            <SignersSteps
              envelopeId={signatureEnvelope.id}
              isSignatureSigned={currentSignature?.status === 'SIGNED'}
              currentSignatureId={currentSignature?.id || ''}
              currentFileUrl={signatureEnvelope.currentFile.url}
              completedSignatureCount={completedSignatureCount?.length || 0}
              isSigningStarted={isSigningStarted}
              setIsSigningStarted={setIsSigningStarted}
            />
          )}
        </VStack>
      )}

      <PDFTronFileRenderer
        node={node}
        mode={canSignEnvelope ? 'signing' : 'reading'}
      />
    </VStack>
  );
};
